/**
 * @file FilteredTemplate component that manages product filtering, URL synchronization, and dynamic content updates
 * @module FilteredTemplate
 */

if (!customElements.get('filtered-template')) {
  /**
   * FilteredTemplate - Custom element for managing product filtering and search results
   *
   * This component handles:
   * - Filter selection and application
   * - URL state management
   * - Dynamic content loading
   * - Sticky filter UI behavior on scroll
   * - Featured filter display and interactions
   *
   * @class
   * @extends HTMLElement
   * @customElement
   * @fires facets:filters-changed - When filters have been changed
   * @listens facets:filters-changed - Responds to filter changes
   * @listens popstate - Handles browser history navigation
   * @listens scroll - Manages sticky filter behavior
   */
  class FilteredTemplate extends HTMLElement {
    /**
     * Creates an instance of FilteredTemplate
     * @constructor
     */
    constructor() {
      super();

      /**
       * Array to store filter data
       * @type {Array}
       * @private
       */
      this.filterData = [];

      /**
       * The scroll position at which the filters should become sticky
       * @type {number}
       * @private
       */
      this.scrollPositionToLockAt = 0;
    }

    /**
     * Lifecycle callback when the element is added to the DOM
     * @returns {void}
     */
    connectedCallback() {
      this.init();
    }

    /**
     * Initialize the component
     * @returns {void}
     * @private
     */
    init() {
      this.landingUrl = this.dataset.landingUrl;
      this.initElements();
      this.initEvents();
      this.initActiveFilterImage();
      this.initScrollPosition();
    }

    /**
     * Initialize DOM element references
     * @returns {void}
     * @private
     */
    initElements() {
      this.facetsWrapper = this.querySelector('.facets-wrapper');
      this.featuredFiltersWrapper = this.facetsWrapper?.querySelector('.featured__filters');
      this.featuredFilters = this.featuredFiltersWrapper?.querySelector('featured-filters');
      this.facetsContainer = this.facetsWrapper?.querySelector('.facets-container');
      this.filtersForm = this.querySelector('facet-filters-form');
      this.productsGridContainer = this.querySelector('.product-grid-container');
      this.backToTop = this.querySelector('.back-to-top-wrapper');

      this.header = document.querySelector('#site-header');
      this.filters = this.querySelector('aside.facets-wrapper');
      this.collectionHero = this.querySelector('.collection-hero');
      this.searchBanner = this.querySelector('.search__banner');
      this.filterImages = this.querySelector('.filter__images');

      this.drawerContent = this.querySelector(".slide-out-drawer__inner");
    }

    /**
     * Calculate and set the scroll position at which filters should become sticky
     * @returns {void}
     * @private
     */
    initScrollPosition() {
      if (this.filterImages) {
        this.scrollPositionToLockAt = this.filterImages.offsetTop + 140;
      } else {
        this.scrollPositionToLockAt = this.collectionHero
          ? this.collectionHero.offsetHeight
          : (this.searchBanner ? this.searchBanner.offsetHeight : 0);
      }
    }

    /**
     * Initialize event listeners
     * @returns {void}
     * @private
     */
    initEvents() {
      // Listen for filter change events
      this.addEventListener('facets:filters-changed', this.handleFiltersChanged.bind(this));

      // Handle browser back/forward navigation
      window.addEventListener('popstate', (event) => {
        const searchParams = window.location.search.slice(1);
        const params = event.state?.searchParams || searchParams;
        this.refreshSections(params);
      });

      // Handle scroll for sticky filter behavior
      window.addEventListener('scroll', this.handleScroll.bind(this));
    }

    /**
     * Handle scroll events to manage sticky filter UI on mobile
     * @returns {void}
     * @private
     */
    handleScroll() {
      // Apply logic only for mobile screens
      if (window.innerWidth > 767) return;

      // Check for featured filters
      const hasFeaturedFilters = this.querySelector('featured-filters')?.childNodes.length > 1;
      let targetContainer = hasFeaturedFilters
        ? this.querySelector('featured-filters')
        : this.facetsContainer;

      // Get dimensions of the header and filters container
      const headerRect = this.header?.getBoundingClientRect();
      const targetRect = targetContainer?.getBoundingClientRect();

      // Check if the facets container is touching the header
      const isTouching = hasFeaturedFilters
        ? (headerRect?.bottom >= targetRect?.top)
        : (targetRect?.top <= headerRect?.bottom + 27);

      const passedTripwire = window.scrollY >= this.scrollPositionToLockAt;

      // Apply or remove sticky class based on conditions
      if (isTouching || passedTripwire) {
        this.filters?.classList.add('__sticky');
      } else {
        this.filters?.classList.remove('__sticky');
      }
    }

    /**
     * Handle filter change events
     * @param {CustomEvent} event - The filters changed event
     * @returns {void}
     * @private
     */
    handleFiltersChanged(event) {
      if (!this.contains(event.target)) return;

      let urlParams = event.detail.urlParameters;

      // Ensure availability filter is present
      if (urlParams.indexOf('filter.v.m.greyson.availability=1') < 0) {
        urlParams = `${urlParams}&filter.v.m.greyson.availability=1`;
      }
      this.searchParamsPrev = urlParams;
      this.refreshSections(urlParams);
    }

    /**
     * Refresh page sections with new filter parameters
     * @param {string} searchParams - URL search parameters string
     * @returns {Promise<void>}
     * @private
     */
    async refreshSections(searchParams) {
      this.classList.add('loading');

      try {
        // Update URL first
        window.history.pushState(
          {
            searchParams,
            collectionUrl: this.landingUrl
          },
          '',
          `${this.landingUrl}${searchParams ? '?' + searchParams : ''}`
        );

        // Fetch and render each section
        const sections = this.getSectionsToRender();
        await Promise.all(sections.map(section => this.renderSection(section, searchParams)));

        this.initElements();
        if(this.filtersForm) {
          this.filtersForm.dispatchInitializedEvent();
        }
        this.initActiveFilterImage();
        this.initScrollPosition();

        // Cleanup and reset state
        scrollLock.disableBodyScrollLock();
        removeTrapFocus();

        requestAnimationFrame(() => {
          this.scrollToAdjustedTop();
        });

      } catch (error) {
        console.error('Error refreshing sections:', error);
      } finally {
        this.classList.remove('loading');
      }
    }

    /**
     * Get the sections that need to be re-rendered when filters change
     * @returns {Array<Object>} Array of section objects with:
     *   @property {string} id - Section ID
     *   @property {string} section - Section data ID
     *   @property {string} selector - CSS selector for the section
     *   @property {Array<Object>} [sub_selectors] - Optional array of sub-selectors for more granular replacement
     *     @property {string} selector - CSS selector for the sub-element
     *     @property {boolean} [multiple=false] - Whether to select multiple elements (querySelectorAll)
     *     @property {boolean} [replace_by_id=false] - Whether to match elements by ID when replacing
     * @private
     */
    getSectionsToRender() {
      return [
        {
          id: 'product-grid',
          section: document.getElementById('product-grid').dataset.id,
          selector: '#product-grid'
        },
        {
          id: 'filter-drawer',
          section: document.getElementById('filter-drawer').dataset.id,
          selector: '#facets-drawer-container',
          sub_selectors: [
            {
              selector: '#facets-top'
            },
            {
              selector: '#facets-wrapper .facets__disclosure .facets__display',
              multiple: true,
              replace_by_id: true
            },
            {
              selector: '#facets-bottom'
            }
          ]
        }
      ];
    }

    /**
     * Render a specific section with updated content
     * @param {Object} section - Section information object
     * @param {string} section.id - Section ID
     * @param {string} section.section - Section data ID
     * @param {string} section.selector - CSS selector for the section
     * @param {Array<Object>} [section.sub_selectors] - Optional array of sub-selectors for more granular replacement
     *   @property {string} selector - CSS selector for the sub-element
     *   @property {boolean} [multiple=false] - Whether to select multiple elements (querySelectorAll)
     *   @property {boolean} [replace_by_id=false] - Whether to match elements by ID when replacing
     * @param {string} searchParams - URL search parameters string
     * @returns {Promise<void>}
     * @private
     */
    async renderSection(section, searchParams) {
      const url = `${this.landingUrl}?section_id=${section.section}${searchParams ? '&' + searchParams : ''}`;

      try {
        // find the section by selector, and add the loading class
        const sectionElement = document.querySelector(section.selector);
        if (sectionElement) {
          sectionElement.classList.add('loading');
        }
        const response = await fetch(url);
        const html = await response.text();

        // Parse the new section HTML
        const parser = new DOMParser();
        const newContent = parser.parseFromString(html, 'text/html');

        // Check if we need to do granular replacement with sub-selectors
        if (section.sub_selectors && section.sub_selectors.length > 0) {
          // Get the parent container for sub-selectors
          const parentSection = document.querySelector(section.selector);
          const newParentSection = newContent.querySelector(section.selector);

          if (parentSection && newParentSection) {
            // Process each sub-selector
            for (const subSelector of section.sub_selectors) {
              const multiple = subSelector.multiple === true;
              const replaceById = subSelector.replace_by_id === true;

              if (multiple) {
                // Handle multiple elements
                const currentElements = parentSection.querySelectorAll(subSelector.selector);
                const newElements = newParentSection.querySelectorAll(subSelector.selector);

                if (replaceById) {
                  // Match by ID and replace children only
                  currentElements.forEach(currentElem => {
                    if (!currentElem.id) return;

                    // Find matching element in new content by ID
                    const newElem = Array.from(newElements).find(elem => elem.id === currentElem.id);
                    if (newElem) {
                      // Replace children while preserving the current element
                      while (currentElem.firstChild) {
                        currentElem.removeChild(currentElem.firstChild);
                      }

                      // Clone nodes to avoid issues when reusing them
                      Array.from(newElem.childNodes).forEach(node => {
                        currentElem.appendChild(node.cloneNode(true));
                      });
                    }
                  });
                } else {
                  // Replace each element entirely
                  currentElements.forEach((currentElem, index) => {
                    if (index < newElements.length) {
                      currentElem.replaceWith(newElements[index].cloneNode(true));
                    }
                  });
                }
              } else {
                // Handle single element
                const currentElement = parentSection.querySelector(subSelector.selector);
                const newElement = newParentSection.querySelector(subSelector.selector);

                if (currentElement && newElement) {
                  if (replaceById && currentElement.id) {
                    // Replace children only
                    while (currentElement.firstChild) {
                      currentElement.removeChild(currentElement.firstChild);
                    }

                    // Clone nodes to avoid issues when reusing them
                    Array.from(newElement.childNodes).forEach(node => {
                      currentElement.appendChild(node.cloneNode(true));
                    });
                  } else {
                    // Replace the entire element
                    currentElement.replaceWith(newElement.cloneNode(true));
                  }
                }
              }
            }
          }
        } else {
          // Original logic for whole section replacement
          let currentElement, newElement;

          if (section.selector === `#${section.id}`) {
            // If selector matches the ID, replace the entire section
            currentElement = document.getElementById(section.id);
            newElement = newContent.querySelector(section.selector);
          } else {
            // Otherwise, find the selector within its parent section
            const parentSection = document.getElementById(section.id);
            if (parentSection) {
              currentElement = parentSection.querySelector(section.selector);
              newElement = newContent.querySelector(section.selector);
            }
          }

          if (currentElement && newElement) {
            currentElement.replaceWith(newElement);
          }
        }
        // find the section by selector, and remove the loading class
        if (sectionElement) {
          sectionElement.classList.remove('loading');
        }
      } catch (error) {
        console.error(`Error rendering section ${section.id}:`, error);
      }
    }

    /**
     * Scroll to adjusted top position accounting for header height
     * @returns {void}
     * @private
     */
    scrollToAdjustedTop() {
      const headerHeight = parseInt(
        getComputedStyle(document.body).getPropertyValue('--header-height') || '0'
      );
      window.scrollTo({
        top: headerHeight ? -headerHeight : 0,
        behavior: 'smooth'
      });
    }

    /**
     * Initialize active filter image content
     * @returns {Promise<void>}
     * @private
     */
    async initActiveFilterImage() {
      if (!this.featuredFilters) return;
      const activeFilterImage = this.featuredFilters.querySelector('.filter__images .grid--item label.active');
      if (!activeFilterImage?.dataset.swapContentUrl) return;

      try {
        const response = await fetch(activeFilterImage.dataset.swapContentUrl);
        const html = await response.text();
        const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
        const mainContent = parsedHtml.querySelector('.content-for-layout');

        if (!mainContent) return;

        if (this.facetsContainer) this.facetsContainer.classList.add('hidden');
        if (this.backToTop) this.backToTop.classList.add('hidden');

        if (this.productsGridContainer) {
          this.productsGridContainer.replaceChildren(...mainContent.children);
        }
      } catch (error) {
        console.error('Error initializing active filter image:', error);
      }
    }
  }

  customElements.define('filtered-template', FilteredTemplate);
}
